import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaseSensitive, ChevronDown, ChevronUp, Regex, WholeWord, X } from 'lucide-react';
import { resultLabel, searchOptions } from './terminalSearch';
import './TerminalSearch.css';

/** A Match case / whole word / regex switch; keeps the focus in the field. */
function OptionBtn({ on, onToggle, title, children }) {
  return (
    <button
      type="button"
      className={`term-search-opt${on ? ' term-search-opt--on' : ''}`}
      aria-pressed={on}
      aria-label={title}
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
    >
      {children}
    </button>
  );
}

/**
 * The find bar over a terminal pane (card 2). `getSearch()` is the pane's
 * xterm search add-on, `getTerm()` its xterm. Typing searches as you go,
 * Enter / Shift+Enter (or the arrows) step, Esc closes and hands the keys back
 * to the terminal. Highlights are cleared on close.
 */
export default function TerminalSearchBar({ getSearch, getTerm, onClose }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [opts, setOpts] = useState({ caseSensitive: false, regex: false, wholeWord: false });
  const [results, setResults] = useState({ resultIndex: -1, resultCount: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
    const sub = getSearch()?.onDidChangeResults?.((r) => setResults(r));
    return () => sub?.dispose?.();
  }, [getSearch]);

  const find = useCallback(
    (back, incremental = false, q = query, o = opts) => {
      const search = getSearch();
      if (!search) return;
      if (!q) {
        search.clearDecorations();
        setResults({ resultIndex: -1, resultCount: 0 });
        return;
      }
      try {
        if (back) search.findPrevious(q, searchOptions(o));
        else search.findNext(q, searchOptions(o, incremental));
      } catch {
        // an unfinished regex: no results until it parses
        setResults({ resultIndex: -1, resultCount: 0 });
      }
    },
    [getSearch, query, opts]
  );

  const close = useCallback(() => {
    getSearch()?.clearDecorations();
    onClose();
    getTerm()?.focus();
  }, [getSearch, getTerm, onClose]);

  const toggle = (key) => {
    const next = { ...opts, [key]: !opts[key] };
    setOpts(next);
    find(false, true, query, next);
  };

  const label = resultLabel(query, results, t);
  return (
    <div className="term-search" role="search" aria-label={t('terminalExt.searchLabel', 'Find in terminal')}>
      <div className="term-search-field">
        <input
          ref={inputRef}
          className="term-search-input"
          value={query}
          placeholder={t('terminalExt.searchPlaceholder', 'Find')}
          aria-label={t('terminalExt.searchLabel', 'Find in terminal')}
          spellCheck={false}
          onChange={(e) => {
            setQuery(e.target.value);
            find(false, true, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              find(e.shiftKey);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              close();
            }
          }}
        />
        <OptionBtn on={opts.caseSensitive} onToggle={() => toggle('caseSensitive')} title={t('terminalExt.searchCase', 'Match case')}>
          <CaseSensitive size={14} />
        </OptionBtn>
        <OptionBtn on={opts.wholeWord} onToggle={() => toggle('wholeWord')} title={t('terminalExt.searchWord', 'Match whole word')}>
          <WholeWord size={14} />
        </OptionBtn>
        <OptionBtn on={opts.regex} onToggle={() => toggle('regex')} title={t('terminalExt.searchRegex', 'Use regular expression')}>
          <Regex size={14} />
        </OptionBtn>
      </div>
      <span className="term-search-count" aria-live="polite">
        {label}
      </span>
      <button type="button" className="term-search-btn" aria-label={t('terminalExt.searchPrev', 'Previous match')} title={t('terminalExt.searchPrev', 'Previous match')} disabled={!results.resultCount} onClick={() => find(true)}>
        <ChevronUp size={14} />
      </button>
      <button type="button" className="term-search-btn" aria-label={t('terminalExt.searchNext', 'Next match')} title={t('terminalExt.searchNext', 'Next match')} disabled={!results.resultCount} onClick={() => find(false)}>
        <ChevronDown size={14} />
      </button>
      <button type="button" className="term-search-btn" aria-label={t('terminalExt.searchClose', 'Close')} title={t('terminalExt.searchClose', 'Close')} onClick={close}>
        <X size={14} />
      </button>
    </div>
  );
}
